import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Sparkles } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import ReviewForm from "@/components/marketplace/ReviewForm";
import ReviewCard from "@/components/marketplace/ReviewCard";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Two-part page:
 *  - "Reviews to write": completed transactions where the current user hasn't
 *    yet left a review (server-verified via /reviews/to-write).
 *  - "Reviews I've received / written": history from /reviews/mine.
 */
export default function ReviewsToWritePage() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);
  const [mine, setMine] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Reviews");

  const load = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        api.get("/reviews/to-write"),
        api.get("/reviews/mine"),
      ]);
      setPending(p.data?.items || []);
      setMine(m.data || []);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const received = mine.filter((r) => r.reviewee_id === user?.id);
  const written = mine.filter((r) => r.reviewer_id === user?.id);

  return (
    <DashboardShell>
      <div className="max-w-5xl">
        <div className="mb-6">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Reviews</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your feedback, both ways.</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Reviews unlock the moment a transaction is marked <b>completed</b>.</p>
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : (
          <>
            {active ? (
              <div className="mb-8" data-testid="active-review">
                <button onClick={() => setActive(null)} className="text-sm text-[#165a71] font-medium mb-3 inline-flex items-center gap-1"><ArrowRight className="h-4 w-4 rotate-180" /> Back to list</button>
                <div className="text-xs uppercase tracking-wider text-[#252A34]/60 mb-2">Reviewing: <b>{active.service_title}</b></div>
                <ReviewForm
                  transaction={active}
                  direction={active.direction}
                  onSubmitted={() => { setActive(null); load(); }}
                />
              </div>
            ) : (
              <section className="mb-10">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display text-lg font-semibold text-[#071b22]">Reviews to write</h2>
                  <div className="text-xs text-[#252A34]/60">{pending.length} waiting</div>
                </div>
                {pending.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center">
                    <Sparkles className="h-6 w-6 mx-auto text-[#165a71]" />
                    <div className="font-display text-lg text-[#071b22] font-semibold mt-2">Nothing to review right now.</div>
                    <div className="text-sm text-[#252A34]/70 mt-1">Once a job is marked completed, it'll show up here.</div>
                  </div>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2" data-testid="reviews-to-write-list">
                    {pending.map((tx) => (
                      <button
                        key={`${tx.target_collection}-${tx.id}`}
                        onClick={() => setActive(tx)}
                        className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#165a71]/40"
                        data-testid={`review-item-${tx.id}`}
                      >
                        <div className="text-[10px] uppercase tracking-wider text-[#252A34]/60">{tx.target_collection === "orders" ? "Digital order" : "Local booking"}</div>
                        <div className="font-display font-semibold text-[#071b22] mt-1 truncate">{tx.service_title}</div>
                        <div className="text-xs text-[#252A34]/60 mt-1">Completed {tx.completed_at ? new Date(tx.completed_at).toLocaleDateString() : (tx.updated_at ? new Date(tx.updated_at).toLocaleDateString() : "recently")}</div>
                        <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#165a71]/10 text-[#165a71] text-xs font-medium px-3 py-1"><Star className="h-3 w-3" /> Rate now</div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Received */}
            <section className="mb-10">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-semibold text-[#071b22]">Reviews received</h2>
                <div className="text-xs text-[#252A34]/60">{received.length} total</div>
              </div>
              {received.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-[#252A34]/70">No reviews yet.</div>
              ) : (
                <div className="grid gap-3" data-testid="reviews-received">
                  {received.map((r) => <ReviewCard key={r.id} review={r} allowResponse onChange={load} />)}
                </div>
              )}
            </section>

            {/* Written */}
            <section>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-display text-lg font-semibold text-[#071b22]">Reviews you wrote</h2>
                <div className="text-xs text-[#252A34]/60">{written.length} total</div>
              </div>
              {written.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-[#252A34]/70">You haven't reviewed anyone yet.</div>
              ) : (
                <div className="grid gap-3" data-testid="reviews-written">
                  {written.map((r) => <ReviewCard key={r.id} review={r} />)}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
